import { StravaPreviewData } from "../types";
import styles from "./activity-summary-card.module.css";

interface ActivitySummaryCardProps {
  activity: StravaPreviewData;
}

/**
 * Displays activity metadata in a compact card.
 * Shows activity name, type, distance, elevation, and date.
 */
export const ActivitySummaryCard = ({ activity }: ActivitySummaryCardProps) => {
  const formattedDistance = formatDistance(activity.gpsData.distanceMeters);
  const formattedElevation = formatElevation(activity.gpsData.elevationGain);
  const formattedDate = formatDate(activity.startDate);
  const activityIcon = getActivityIcon(activity.activityType);

  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <span className={styles.icon}>{activityIcon}</span>
        <span className={styles.type}>{activity.activityType}</span>
      </div>

      <h3 className={styles.name}>{activity.activityName}</h3>

      <div className={styles.stats}>
        {activity.gpsData.distanceMeters && activity.gpsData.distanceMeters > 0 && (
          <div className={styles.stat}>
            <span className={styles.statValue}>{formattedDistance}</span>
            <span className={styles.statLabel}>Distance</span>
          </div>
        )}

        {activity.gpsData.elevationGain && activity.gpsData.elevationGain > 0 && (
          <div className={styles.stat}>
            <span className={styles.statValue}>{formattedElevation}</span>
            <span className={styles.statLabel}>Elevation</span>
          </div>
        )}

        <div className={styles.stat}>
          <span className={styles.statValue}>{formattedDate}</span>
          <span className={styles.statLabel}>Date</span>
        </div>
      </div>
    </div>
  );
};

/**
 * Format distance in meters to a human-readable string.
 * Uses km for larger distances, m for smaller.
 */
function formatDistance(meters: number | undefined): string {
  if (!meters || meters === 0) return "—";

  if (meters >= 1000) {
    const km = meters / 1000;
    return `${km.toFixed(1)} km`;
  }

  return `${Math.round(meters)} m`;
}

/**
 * Format elevation gain in meters.
 */
function formatElevation(meters: number | undefined): string {
  if (!meters || meters === 0) return "—";
  return `${Math.round(meters)} m`;
}

/**
 * Format ISO date string to a readable format.
 */
function formatDate(isoString: string): string {
  try {
    const date = new Date(isoString);
    return date.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return "—";
  }
}

/**
 * Get an emoji icon for the activity type.
 */
function getActivityIcon(activityType: string): string {
  const type = activityType.toLowerCase();

  if (type.includes("run")) return "🏃";
  if (type.includes("ride") || type.includes("cycling") || type.includes("bike")) return "🚴";
  if (type.includes("swim")) return "🏊";
  if (type.includes("hike") || type.includes("walk")) return "🥾";
  if (type.includes("ski") || type.includes("snowboard")) return "⛷️";
  if (type.includes("kayak") || type.includes("canoe") || type.includes("paddle")) return "🛶";
  if (type.includes("row")) return "🚣";
  if (type.includes("golf")) return "⛳";
  if (type.includes("yoga") || type.includes("workout")) return "🧘";

  // Default activity icon
  return "📍";
}
